import {useCallback, useState} from "react";
import {useSetRecoilState} from "recoil";
import {globalNotificationState} from "../../../_common/global_state";
import {
    apiAbandonUserVerificationProfile,
    apiAssignUserVerificationProfile,
    apiGetUserVerificationHistories,
    apiGetUserVerificationProfile,
    apiInitializeUserVerificationHistory,
    apiSaveUserVerificationHistory,
    apiSubmitUserVerificationHistory,
    apiUpdateUserVerificationHistoryStatus,
    apiUploadUserVerificationHistoryMedia
} from "../../../api/restful/verification";
import {useVerification} from "../../../_common/_authorize";

export const useAssignUserVerificationProfile = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const assignUserVerificationProfile = useCallback(async (flowSettingId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiAssignUserVerificationProfile(flowSettingId);
            if (response?.status === "failed") {
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: response?.data?.errObj,
                    key: "accountVerificationNotification",
                });
            }
            return response;
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            setError(errData);
            setNotificationMessage({
                status: "error",
                title: "",
                msg: errData?.errObj,
                key: "accountVerificationNotification",
            });
            return errData;
        } finally {
            setLoading(false);
        }
    }, [setNotificationMessage]);

    return { assignUserVerificationProfile, loading, error };
};

export const useInitializeUserVerificationHistory = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);
    const { getClientDeviceInfo } = useVerification();

    const initializeUserVerificationHistory = useCallback(async (tierSettingId) => {
        setLoading(true);
        setError(null);
        try {
            const deviceInfo = await getClientDeviceInfo();

            const response = await apiInitializeUserVerificationHistory({
                tierSetting: tierSettingId,
                ipAddress: deviceInfo.ip,
                device: deviceInfo.device,
                browser: deviceInfo?.browser

            });

            if (response?.status === "failed") {
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: response?.errObj,
                    key: "accountVerificationNotification",
                });
            }
            return response;
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            console.error("initializeUserVerificationHistory:", errData);
            setError(errData);
            setNotificationMessage({
                status: "error",
                title: "",
                msg: errData?.errObj,
                key: "accountVerificationNotification",
            });
            return errData;
        } finally {
            setLoading(false);
        }
    }, [setNotificationMessage]);

    return { initializeUserVerificationHistory, loading, error };
};

export const useAbandonUserVerificationProfile = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const abandonUserVerificationProfile = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiAbandonUserVerificationProfile();
            return response;
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            console.error("abandonUserVerificationProfile:", errData);
            setError(errData);
            return errData;
        } finally {
            setLoading(false);
        }
    }, []);

    return { abandonUserVerificationProfile, loading, error };
};

export const useSaveUserVerificationHistory = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const saveUserVerificationHistory = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            return await apiSaveUserVerificationHistory(data);
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            console.error("saveUserVerificationHistory:", errData);
            setError(errData);
            return errData;
        } finally {
            setLoading(false);
        }
    }, []);

    return { saveUserVerificationHistory, loading, error };
};

export const useSubmitUserVerificationHistory = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const submitUserVerificationHistory = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            return await apiSubmitUserVerificationHistory(data);
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            console.error("submitUserVerificationHistory:", errData);
            setError(errData);

            setNotificationMessage({
                status: "error",
                title: "",
                msg: errData?.errObj,
                key: "accountVerificationNotification",
            });
            return errData;
        } finally {
            setLoading(false);
        }
    }, []);

    return { submitUserVerificationHistory, loading, error };
};

export const useUploadUserVerificationHistoryMedia = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const uploadUserVerificationHistoryMedia = useCallback(async (formData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiUploadUserVerificationHistoryMedia(formData);
            if (response?.status === "failed") {
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: response?.errObj,
                    key: "accountVerificationNotification",
                });
            }
            return response;
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            console.error("uploadUserVerificationHistoryMedia:", errData);
            setError(errData);
            setNotificationMessage({
                status: "error",
                title: "",
                msg: errData?.errObj,
                key: "accountVerificationNotification",
            });
            return errData;
        } finally {
            setLoading(false);
        }
    }, [setNotificationMessage]);

    return { uploadUserVerificationHistoryMedia, loading, error };
};

export const useUpdateUserVerificationHistoryStatus = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const updateUserVerificationHistoryStatus = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiUpdateUserVerificationHistoryStatus(data);
            if (response?.status === "failed") {
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: response?.errObj,
                    key: "accountVerificationNotification",
                });
            }
            return response;
        } catch (e) {
            const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
            console.error("updateUserVerificationHistoryStatus:", errData);
            setError(errData);
            setNotificationMessage({
                status: "error",
                title: "",
                msg: errData?.errObj,
                key: "accountVerificationNotification",
            });
            return errData;
        } finally {
            setLoading(false);
        }
    }, [setNotificationMessage]);

    return { updateUserVerificationHistoryStatus, loading, error };
};

export const useGetUserVerificationHistories = () => {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [previousParam, setPreviousParam] = useState({
        filter: {},
        sort: {},
        offset: 0,
        limit: 0,
    });

    const getUserVerificationHistories = useCallback(
        async (param = { filter: {}, sort: {}, limit: 0, offset: 0 }) => {
            setLoading(true);
            setPreviousParam(param);

            try {
                const result = await apiGetUserVerificationHistories(param);

                if (result?.status === "failed") {
                    setData([]);
                    setTotal(0);
                    return;
                }

                setData(result?.data);
                setTotal(result?.total);
                return result;
            } catch (e) {
                console.error("getUserVerificationHistories error:", e);
                throw e;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const refetchUserVerificationHistories = useCallback(() => {
        if (previousParam) {
            getUserVerificationHistories(previousParam);
        }
    }, [previousParam, getUserVerificationHistories]);

    return {
        data,
        total,
        loading,
        getUserVerificationHistories,
        refetchUserVerificationHistories,
    };
};

export const useGetUserVerificationProfile = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previousParam, setPreviousParam] = useState({
        filter: {},
        sort: {},
        offset: 0,
        limit: 0,
    });

    const getUserVerificationProfile = useCallback(
        async (param = { filter: {}, sort: {}, limit: 0, offset: 0 }) => {
            setLoading(true);
            setPreviousParam(param);

            try {
                const result = await apiGetUserVerificationProfile(param);

                if (result?.status === "failed") {
                    setData(null);
                    return;
                }

                setData(result?.data[0]);
                return result;
            } catch (e) {
                console.error("getUserVerificationProfile error:", e);
                throw e;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const refetchUserVerificationProfile = useCallback(() => {
        if (previousParam) {
            getUserVerificationProfile(previousParam);
        }
    }, [previousParam, getUserVerificationProfile]);

    return {
        data,
        loading,
        getUserVerificationProfile,
        refetchUserVerificationProfile,
    };
};